import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";

import BackgroundCanvas from "./BackgroundCanvas";
import Reveal from "../common/Reveal";
import { Eyebrow } from "../common/Section";
import { findService } from "../../data/serviceCatalog";

import "./Css/WorkSection.css";

/**
 * These tiles are capabilities, not case studies.
 *
 * There are no client projects, live URLs or outcomes anywhere in this
 * repository, so the section says what DepthLogix builds and sends every
 * tile to the real service page that describes that work — nine dead
 * `href="#"` links replaced with eight live destinations. The stock
 * "Team Collaboration" office photo (a remote Unsplash file) is gone: it
 * was not a capability.
 *
 * Paths are resolved through findService() rather than typed out, so a
 * slug change in the catalog can never leave a dead link here — a tile
 * whose service no longer exists simply drops out of the arc.
 */
const TILE_SOURCE = [
  {
    key: "web",
    label: "Web Development",
    categoryKey: "digital-transformation",
    slug: "web-development",
    image: "/images/Worksection/pf1.jpg",
    width: 900,
    height: 675,
  },
  {
    key: "mobile",
    label: "Mobile Apps",
    categoryKey: "digital-transformation",
    slug: "app-development",
    image: "/images/Worksection/pf9.jpg",
    width: 900,
    height: 1350,
  },
  {
    key: "design",
    label: "UI/UX Design",
    categoryKey: "digital-transformation",
    slug: "ui-ux-design",
    image: "/images/Worksection/pf4.jpg",
    width: 900,
    height: 900,
  },
  {
    key: "cloud",
    label: "Cloud Solutions",
    categoryKey: "digital-transformation",
    slug: "SaaS",
    image: "/images/Worksection/pf8.jpg",
    width: 900,
    height: 506,
  },
  {
    key: "ai",
    label: "AI & Data",
    categoryKey: "emerging-tech",
    slug: "data-analytics",
    image: "/images/Worksection/pf7.jpg",
    width: 900,
    height: 750,
  },
  {
    key: "custom",
    label: "Custom Solutions",
    categoryKey: "digital-transformation",
    slug: "custom-software",
    image: "/images/Worksection/pf10.avif",
    width: 600,
    height: 399,
  },
  {
    key: "marketing",
    label: "Digital Marketing",
    categoryKey: "digital-marketing",
    slug: "seo",
    image: "/images/Worksection/pf6.jpg",
    width: 900,
    height: 600,
  },
  {
    key: "tiktok",
    label: "TikTok Shop",
    categoryKey: "digital-marketing",
    slug: "tiktok-shop-management",
    image: "/images/Worksection/pf5.jpg",
    width: 900,
    height: 1344,
  },
];

const TILES = TILE_SOURCE.map((tile) => {
  const service = findService(tile.categoryKey, tile.slug);
  if (!service) return null;
  return {
    ...tile,
    path: service.path,
    service: service.title,
    category: service.category,
    description: service.description,
  };
}).filter(Boolean);

const COUNT = TILES.length;
const HALF = Math.floor(COUNT / 2);

// Horizontal travel, in px, past which a drag counts as a swipe not a tap.
const SWIPE_PX = 44;

const wideViewport = () =>
  typeof window === "undefined" ||
  !window.matchMedia ||
  window.matchMedia("(min-width: 1024px)").matches;

const pad = (n) => String(n).padStart(2, "0");

const OurWorkSection = () => {
  const [active, setActive] = useState(0);
  // How many cards deep the arc runs on each side. Narrow viewports show
  // fewer so the visible cards can stay large instead of shrinking to
  // thumbnails — the old fixed nine-card arc overflowed badly at 360px.
  // Seeded from the viewport so a phone never paints the wide arc first.
  const [reach, setReach] = useState(() => (wideViewport() ? 3 : 2));

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mq = window.matchMedia("(min-width: 1024px)");
    const apply = () => setReach(mq.matches ? 3 : 2);
    apply();

    if (mq.addEventListener) {
      mq.addEventListener("change", apply);
      return () => mq.removeEventListener("change", apply);
    }
    mq.addListener(apply);
    return () => mq.removeListener(apply);
  }, []);

  const step = useCallback((direction) => {
    setActive((prev) => (prev + direction + COUNT) % COUNT);
  }, []);

  // Signed distance from the active card, wrapped so the arc is a loop.
  // Replaces the nine hard-coded :nth-child rules — the geometry now
  // works for any number of tiles.
  const offsetOf = useCallback(
    (index) => {
      const raw = (((index - active) % COUNT) + COUNT) % COUNT;
      return raw > HALF ? raw - COUNT : raw;
    },
    [active]
  );

  const drag = useRef({ id: null, startX: 0, dx: 0, swiped: false });

  // Touch pointers get implicit capture from the browser, so moves keep
  // arriving here without setPointerCapture — which is deliberately not
  // used, because capturing on the stage can retarget the follow-up click
  // away from the card's link.
  const onPointerDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    drag.current = { id: event.pointerId, startX: event.clientX, dx: 0, swiped: false };
  };

  const onPointerMove = (event) => {
    if (drag.current.id !== event.pointerId) return;
    drag.current.dx = event.clientX - drag.current.startX;
  };

  const endDrag = (event) => {
    if (drag.current.id !== event.pointerId) return;
    const { dx } = drag.current;
    drag.current.id = null;
    if (Math.abs(dx) > SWIPE_PX) {
      drag.current.swiped = true;
      step(dx < 0 ? 1 : -1);
    }
  };

  const cancelDrag = () => {
    drag.current.id = null;
  };

  // A swipe that started on a card must not also follow that card's link.
  // detail === 0 means the click came from the keyboard, which is never a
  // swipe, so Enter on a focused card still navigates.
  const onClickCapture = (event) => {
    if (!drag.current.swiped) return;
    drag.current.swiped = false;
    if (event.detail === 0) return;
    event.preventDefault();
    event.stopPropagation();
  };

  const onKeyDown = (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      step(-1);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      step(1);
    }
  };

  // Defensive: if the catalog ever stopped resolving these slugs there
  // would be nothing to show, and a half-rendered arc is worse than none.
  if (COUNT === 0) return null;

  const current = TILES[active];

  return (
    <section
      id="work"
      aria-labelledby="work-heading"
      className="dl-work section-padding relative overflow-hidden bg-white"
    >
      {/* Node field — decorative, pointer-events off, pauses off-screen. */}
      <BackgroundCanvas />

      <div className="container-custom relative">
        <div className="grid gap-x-12 gap-y-7 lg:grid-cols-[1.05fr_0.95fr] lg:items-end">
          <Reveal>
            <Eyebrow>What we build</Eyebrow>
            <h2
              id="work-heading"
              className="mt-5 text-display-sm md:text-display font-bold font-bricolage text-ink-900"
            >
              Innovative Software Solutions.
            </h2>
          </Reveal>

          <Reveal delay={90}>
            <p className="max-w-[58ch] text-base sm:text-lg leading-relaxed text-ink-500">
              We craft digital products that transform businesses. From web and
              mobile applications to cloud and AI solutions, our expertise helps
              brands innovate and scale with confidence.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/portfolio" className="btn-primary">
                See Our Work
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link to="/services" className="btn-secondary">
                Browse all services
              </Link>
            </div>
          </Reveal>
        </div>
      </div>

      {/* The arc. Full-bleed so it can run past the container gutters, and
          clipped by the stage so it can never produce a horizontal scrollbar. */}
      <Reveal delay={140} className="relative mt-14 sm:mt-16">
        <div
          className="dl-work-stage"
          role="group"
          aria-roledescription="carousel"
          aria-label="What DepthLogix builds"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={cancelDrag}
          onPointerLeave={cancelDrag}
          onClickCapture={onClickCapture}
          onKeyDown={onKeyDown}
        >
          <ul className="dl-work-arc">
            {TILES.map((tile, index) => {
              const pos = offsetOf(index);
              const dist = Math.abs(pos);
              const inArc = dist <= reach;
              const isActive = pos === 0;

              return (
                <li
                  // Stable key: the card keeps its DOM node across slides, so
                  // the image is fetched and decoded once, not on every click.
                  key={tile.key}
                  className={`dl-work-card${isActive ? " is-active" : ""}${
                    inArc ? "" : " is-out"
                  }`}
                  style={{
                    "--pos": String(pos),
                    "--dist": String(dist),
                    zIndex: 20 - dist,
                  }}
                  aria-hidden={inArc ? undefined : "true"}
                >
                  <Link
                    to={tile.path}
                    className="dl-work-card-link"
                    tabIndex={inArc ? undefined : -1}
                    aria-label={`${tile.label}, ${tile.service}`}
                    // Tabbing through the arc rotates it, so the focused card
                    // is always the one you can actually read.
                    onFocus={() => setActive(index)}
                  >
                    {/* Decorative: the link's accessible name already carries
                        the capability and the service it leads to. */}
                    <img
                      src={tile.image}
                      alt=""
                      aria-hidden="true"
                      width={tile.width}
                      height={tile.height}
                      loading="lazy"
                      decoding="async"
                      draggable="false"
                      className="dl-work-card-img"
                    />
                    <span className="dl-work-card-scrim" aria-hidden="true" />
                    <span className="dl-work-card-meta">
                      <span className="dl-work-card-label">{tile.label}</span>
                      <span className="dl-work-card-go">
                        <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
                        View service
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </Reveal>

      <div className="container-custom relative mt-10 sm:mt-12">
        <div className="rule" aria-hidden="true" />

        <div className="mt-7 flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0" aria-live="polite">
            <p className="font-mono text-[0.68rem] uppercase tracking-eyebrow text-ink-400">
              {pad(active + 1)} / {pad(COUNT)}
              <span aria-hidden="true" className="px-2 text-ink-300">
                ·
              </span>
              {current.category}
            </p>
            <h3 className="mt-3 font-bricolage text-xl sm:text-2xl font-semibold text-ink-900">
              {current.label}
            </h3>
            <p className="mt-2 max-w-[58ch] text-sm sm:text-base leading-relaxed text-ink-500">
              <span className="font-medium text-ink-700">{current.service}.</span>{" "}
              {current.description}
            </p>
            <Link
              to={current.path}
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 transition-colors duration-200 hover:text-brand-800"
            >
              View {current.service}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </div>

          <div className="flex items-center gap-2 md:shrink-0">
            <button
              type="button"
              className="dl-work-nav"
              onClick={() => step(-1)}
              aria-label="Previous project"
            >
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>

            <ol className="dl-work-ticks">
              {TILES.map((tile, index) => (
                <li key={tile.key}>
                  <button
                    type="button"
                    className={`dl-work-tick${index === active ? " is-current" : ""}`}
                    onClick={() => setActive(index)}
                    aria-label={`Show ${tile.label}`}
                    aria-current={index === active ? "true" : undefined}
                  />
                </li>
              ))}
            </ol>

            <button
              type="button"
              className="dl-work-nav"
              onClick={() => step(1)}
              aria-label="Next project"
            >
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurWorkSection;
